import { useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '../stores/authStore.js';
import { useAIStore } from '../stores/aiStore.js';

let socketInstance: Socket | null = null;
let socketToken: string | null = null;

type ToolCall = { name: string; args: Record<string, unknown>; result: unknown };

function extractNavigatePath(result: unknown): string | null {
  if (!result || typeof result !== 'object') return null;
  const navigation = (result as { navigation?: { fullUrl?: unknown } }).navigation;
  const fullUrl = navigation?.fullUrl;
  if (typeof fullUrl !== 'string' || !fullUrl.trim()) return null;

  // Prefer in-app path for React Router. Absolute URLs are converted when same-origin.
  if (fullUrl.startsWith('/')) return fullUrl;
  try {
    const parsed = new URL(fullUrl, window.location.origin);
    if (parsed.origin === window.location.origin) {
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
  } catch {
    // Ignore malformed URLs.
  }

  return null;
}

function emitNavigateFromToolCall(toolCall?: ToolCall): void {
  if (!toolCall || toolCall.name !== 'navigate') return;
  const path = extractNavigatePath(toolCall.result);
  if (!path) return;
  window.dispatchEvent(new CustomEvent('ai:navigate', { detail: { path } }));
}

function emitNavigateFromToolCalls(toolCalls?: ToolCall[]): void {
  if (!toolCalls || toolCalls.length === 0) return;
  for (const tc of toolCalls) {
    emitNavigateFromToolCall(tc);
  }
}

function getSocket(token: string): Socket {
  // Reuse the existing socket while it is connecting/connected for the same token.
  if (socketInstance && socketToken === token) {
    return socketInstance;
  }

  // Token changed (or stale socket exists): recreate a clean instance.
  if (socketInstance) {
    socketInstance.disconnect();
    socketInstance = null;
  }

  socketToken = token;
  socketInstance = io(window.location.origin, {
    path: '/api/socket.io',
    auth: { token },
    // Start with polling then upgrade to websocket for better cross-browser resilience.
    transports: ['polling', 'websocket'],
    reconnection: true,
    reconnectionDelay: 2000,
    reconnectionAttempts: 10,
  });

  return socketInstance;
}

export function useSocket() {
  const token = useAuthStore((s) => s.accessToken);
  const {
    setConnected, addMessage, setIsTyping, setContinuationPending, incrementContinuationCount,
    setStreamingMessageId, appendToMessage, updateMessage,
    setIsStopping,
  } = useAIStore();
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!token) return;

    const socket = getSocket(token);
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
    });

    socket.on('disconnect', () => {
      setConnected(false);
    });

    // AI chat responses
    socket.on('ai:chat:response', (data: {
      type?: string;
      content?: string;
      conversationId?: number;
      toolCalls?: Array<{ name: string; args: Record<string, unknown>; result: unknown }>;
      continuation?: boolean;
      usage?: { promptTokens: number; completionTokens: number; totalTokens: number };
      thinking?: string;
      debugInfo?: Record<string, unknown>;
      contextInfo?: { totalMessages: number; loadedMessages: number; tokenBudget: number; tokenUsed: number; summaryCount: number };
      cacheMetrics?: { cacheCreationTokens: number; cacheReadTokens: number };
      timestamp: string;
    }) => {
      if (data.type === 'typing') {
        // Typing indicator — isTyping is already set by sendMessage
        return;
      }
      setIsTyping(false);

      // Capture server-assigned conversationId for new conversations
      if (data.conversationId) {
        const store = useAIStore.getState();
        if (!store.activeConversationId || store.activeConversationId !== data.conversationId) {
          useAIStore.getState().setActiveConversation(data.conversationId);
        }
      }

      if (data.type === 'error') {
        addMessage({
          id: `ai-${Date.now()}`,
          role: 'system',
          content: data.content || 'Bilinmeyen hata',
          timestamp: data.timestamp,
        });
        setContinuationPending(false);
        return;
      }

      if (data.content) {
        addMessage({
          id: `ai-${Date.now()}`,
          role: 'assistant',
          content: data.content,
          toolCalls: data.toolCalls,
          timestamp: data.timestamp,
          thinking: data.thinking,
          usage: data.usage,
          debugInfo: data.debugInfo,
          contextInfo: data.contextInfo,
          cacheMetrics: data.cacheMetrics,
        });
      }

      emitNavigateFromToolCalls(data.toolCalls);

      // Handle continuation signal
      if (data.continuation) {
        incrementContinuationCount();
        setContinuationPending(true);
        // Play audio notification
        try {
          const ctx = new AudioContext();
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.frequency.value = 880;
          osc.type = 'sine';
          gain.gain.value = 0.1;
          osc.connect(gain).connect(ctx.destination);
          osc.start();
          osc.stop(ctx.currentTime + 0.15);
        } catch { /* audio not available */ }
      } else {
        setContinuationPending(false);
      }
    });

    // AI tool execution results
    socket.on('ai:execute:result', (data: { tool: string; success: boolean; result?: unknown; error?: string; timestamp: string }) => {
      addMessage({
        id: `tool-${Date.now()}`,
        role: 'tool',
        content: data.success
          ? `Tool **${data.tool}** executed successfully.`
          : `Tool **${data.tool}** failed: ${data.error}`,
        toolName: data.tool,
        toolResult: data.result,
        timestamp: data.timestamp,
      });

      if (data.success && data.tool === 'navigate') {
        const path = extractNavigatePath(data.result);
        if (path) {
          window.dispatchEvent(new CustomEvent('ai:navigate', { detail: { path } }));
        }
      }
    });

    // ── Streaming events ──
    socket.on('ai:chat:stream:start', (data: { conversationId?: number; timestamp: string }) => {
      setIsTyping(false);
      if (data.conversationId) {
        const store = useAIStore.getState();
        if (!store.activeConversationId || store.activeConversationId !== data.conversationId) {
          store.setActiveConversation(data.conversationId);
        }
      }
      const msgId = `ai-stream-${Date.now()}`;
      addMessage({
        id: msgId,
        role: 'assistant',
        content: '',
        timestamp: data.timestamp,
      });
      setStreamingMessageId(msgId);
    });

    socket.on('ai:chat:chunk', (data: {
      type: 'token' | 'thinking' | 'tool_result' | 'tool_pending';
      content?: string;
      toolResult?: { name: string; args: Record<string, unknown>; result: unknown };
      toolPending?: {
        correlationId: string;
        name: string;
        args: Record<string, unknown>;
        dangerLevel: 'destructive';
        timeoutMs: number;
      };
      conversationId?: number;
    }) => {
      // tool_pending fires *outside* a streaming message context too — when the
      // chat loop pauses for confirmation. Route it to the dedicated store
      // slot, not into the assistant message buffer.
      if (data.type === 'tool_pending' && data.toolPending) {
        useAIStore.getState().setPendingTool({
          ...data.toolPending,
          receivedAt: Date.now(),
        });
        return;
      }

      const msgId = useAIStore.getState().streamingMessageId;
      if (!msgId) return;

      if (data.type === 'token' && data.content) {
        appendToMessage(msgId, 'content', data.content);
      } else if (data.type === 'thinking' && data.content) {
        appendToMessage(msgId, 'thinking', data.content);
      } else if (data.type === 'tool_result' && data.toolResult) {
        // Add tool call to the streaming message
        const store = useAIStore.getState();
        const msg = store.messages.find(m => m.id === msgId);
        const existing = msg?.toolCalls || [];
        updateMessage(msgId, { toolCalls: [...existing, data.toolResult] });
        emitNavigateFromToolCall(data.toolResult);
      }
    });

    socket.on('ai:chat:stream:end', (data: {
      conversationId?: number;
      content: string;
      toolCalls?: Array<{ name: string; args: Record<string, unknown>; result: unknown }>;
      continuation?: boolean;
      usage?: { promptTokens: number; completionTokens: number; totalTokens: number };
      thinking?: string;
      debugInfo?: Record<string, unknown>;
      cacheMetrics?: { cacheCreationTokens: number; cacheReadTokens: number };
      contextInfo?: { totalMessages: number; loadedMessages: number; tokenBudget: number; tokenUsed: number; summaryCount: number };
      timestamp: string;
    }) => {
      const msgId = useAIStore.getState().streamingMessageId;
      if (msgId) {
        // Finalize streaming message with complete data
        updateMessage(msgId, {
          content: data.content,
          toolCalls: data.toolCalls,
          usage: data.usage,
          thinking: data.thinking,
          debugInfo: data.debugInfo,
          cacheMetrics: data.cacheMetrics,
          contextInfo: data.contextInfo,
          timestamp: data.timestamp,
        });
        setStreamingMessageId(null);
      }

      emitNavigateFromToolCalls(data.toolCalls);

      if (data.continuation) {
        incrementContinuationCount();
        setContinuationPending(true);
      } else {
        setContinuationPending(false);
      }
    });

    // System events (admin only)
    socket.on('ai:event', (event: { type: string; payload: unknown; timestamp: string }) => {
      // Store events for the AI page context panel
      const aiStore = useAIStore.getState();
      if (aiStore.fullPageMode) {
        addMessage({
          id: `event-${Date.now()}`,
          role: 'system',
          content: `[Event] ${event.type}`,
          timestamp: event.timestamp,
        });
      }
    });

    // AI generation stopped by user
    socket.on('ai:chat:stopped', () => {
      setIsTyping(false);
      setContinuationPending(false);
      setIsStopping(false);
      const msgId = useAIStore.getState().streamingMessageId;
      if (msgId) {
        setStreamingMessageId(null);
        // Remove the partial streaming message
        const msgs = useAIStore.getState().messages;
        const idx = msgs.findIndex(m => m.id === msgId);
        if (idx !== -1) {
          const newMsgs = [...msgs];
          newMsgs.splice(idx, 1);
          useAIStore.setState({ messages: newMsgs });
        }
      }
    });

    // AI UI commands
    socket.on('ai:ui:navigate', (data: { path: string }) => {
      window.dispatchEvent(new CustomEvent('ai:navigate', { detail: data }));
    });

    socket.on('ai:ui:toast', (data: { message: string; type?: 'info' | 'success' | 'error' }) => {
      window.dispatchEvent(new CustomEvent('ai:toast', { detail: data }));
    });

    if (!socket.connected) {
      socket.connect();
    }

    return () => {
      socket.off('connect');
      socket.off('disconnect');
      socket.off('ai:chat:response');
      socket.off('ai:chat:stream:start');
      socket.off('ai:chat:chunk');
      socket.off('ai:chat:stream:end');
      socket.off('ai:execute:result');
      socket.off('ai:event');
      socket.off('ai:chat:stopped');
      socket.off('ai:ui:navigate');
      socket.off('ai:ui:toast');
    };
  }, [token, setConnected, addMessage, setIsTyping, setContinuationPending, setStreamingMessageId, appendToMessage, updateMessage, setIsStopping]);

  const sendMessage = useCallback((
    message: string,
    conversationId?: number,
    profileId?: number,
    attachments?: Array<{ id?: string; fileName: string; mimeType: string; base64Data: string; size: number }>,
    context?: Record<string, unknown>,
  ) => {
    if (!socketRef.current?.connected) return;
    setIsTyping(true);
    useAIStore.getState().addMessage({
      id: `user-${Date.now()}`,
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
    });
    socketRef.current.emit('ai:chat', { message, conversationId, profileId, attachments, context });
  }, [setIsTyping]);

  const executeTool = useCallback((tool: string, params?: Record<string, unknown>) => {
    if (!socketRef.current?.connected) return;
    socketRef.current.emit('ai:execute', { tool, params });
  }, []);

  const executeToolAsync = useCallback(
    (tool: string, params?: Record<string, unknown>, timeoutMs = 60000): Promise<{ success: boolean; result?: unknown; error?: string }> => {
      return new Promise((resolve, reject) => {
        const sock = socketRef.current;
        if (!sock?.connected) {
          reject(new Error('Sokete bağlı değil'));
          return;
        }
        const timer = setTimeout(() => {
          sock.off('ai:execute:result', handler);
          reject(new Error('AI tool çağrısı zaman aşımına uğradı'));
        }, timeoutMs);
        const handler = (payload: { tool: string; success: boolean; result?: unknown; error?: string }) => {
          if (payload.tool !== tool) return;
          clearTimeout(timer);
          sock.off('ai:execute:result', handler);
          resolve({ success: payload.success, result: payload.result, error: payload.error });
        };
        sock.on('ai:execute:result', handler);
        sock.emit('ai:execute', { tool, params });
      });
    },
    [],
  );

  const sendContinue = useCallback((conversationId: number, profileId?: number) => {
    if (!socketRef.current?.connected) return;
    setIsTyping(true);
    socketRef.current.emit('ai:continue', { conversationId, profileId });
  }, [setIsTyping]);


  const sendStop = useCallback((conversationId?: number) => {
    if (!socketRef.current?.connected) return;
    setIsStopping(true);
    socketRef.current.emit('ai:stop', { conversationId });
  }, []);

  return { sendMessage, executeTool, executeToolAsync, sendContinue, sendStop, socket: socketRef };
}
